import React, { useState, useEffect } from "react";

const HRAExemptionCalculator = () => {
  const [basicSalary, setBasicSalary] = useState(50000);
  const [hraReceived, setHraReceived] = useState(20000);
  const [rentPaid, setRentPaid] = useState(15000);
  const [cityType, setCityType] = useState("metro");
  const [calculationType, setCalculationType] = useState("monthly");
  const [result, setResult] = useState({
    basicSalary: "0.00",
    hraReceived: "0.00",
    rentPaid: "0.00",
    actualHRA: "0.00",
    fiftyPercentBasic: "0.00",
    fortyPercentBasic: "0.00",
    rentMinusTenPercent: "0.00",
    hraExemption: "0.00",
    taxableHRA: "0.00",
    exemptionPercentage: "0.00",
    savingTip: "",
  });

  // HRA Exemption Calculation Function
  const calculateHRA = () => {
    let basic = parseFloat(basicSalary);
    let hra = parseFloat(hraReceived);
    let rent = parseFloat(rentPaid);

    if (isNaN(basic) || basic < 0 || isNaN(hra) || hra < 0 || isNaN(rent) || rent < 0) return;

    // Convert to annual if monthly
    if (calculationType === "monthly") {
      basic = basic * 12;
      hra = hra * 12;
      rent = rent * 12;
    }

    // HRA Exemption is minimum of:
    // 1. Actual HRA received
    // 2. 50% of basic salary (metro) or 40% (non-metro)
    // 3. Rent paid minus 10% of basic salary

    const actualHRA = hra;
    const percentageBasic = cityType === "metro" ? basic * 0.5 : basic * 0.4;
    const rentMinusTenPercent = Math.max(0, rent - (basic * 0.1));

    // HRA Exemption is the minimum of all three
    const hraExemption = Math.min(actualHRA, percentageBasic, rentMinusTenPercent);
    
    // Taxable HRA
    const taxableHRA = Math.max(0, actualHRA - hraExemption);
    
    // Exemption percentage
    const exemptionPercentage = actualHRA > 0 ? (hraExemption / actualHRA) * 100 : 0;

    let savingTip = "";
    if (hraExemption === 0) {
      savingTip = "No HRA exemption available. Ensure you're paying rent and claim properly.";
    } else if (exemptionPercentage < 50) {
      savingTip = "Low exemption. Consider increasing rent payment or adjusting salary structure.";
    } else if (exemptionPercentage < 80) {
      savingTip = "Moderate exemption. You can optimize by adjusting rent amount.";
    } else if (exemptionPercentage < 100) {
      savingTip = "Good exemption! You're utilizing HRA benefits effectively.";
    } else {
      savingTip = "Maximum exemption! Your entire HRA is tax-free.";
    }

    setResult({
      basicSalary: basic.toFixed(2),
      hraReceived: actualHRA.toFixed(2),
      rentPaid: rent.toFixed(2),
      actualHRA: actualHRA.toFixed(2),
      fiftyPercentBasic: (basic * 0.5).toFixed(2),
      fortyPercentBasic: (basic * 0.4).toFixed(2),
      rentMinusTenPercent: rentMinusTenPercent.toFixed(2),
      hraExemption: hraExemption.toFixed(2),
      taxableHRA: taxableHRA.toFixed(2),
      exemptionPercentage: exemptionPercentage.toFixed(2),
      savingTip,
    });
  };

  useEffect(() => {
  calculateHRA();
}, [basicSalary, hraReceived, rentPaid, cityType, calculationType]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>HRA Exemption Calculator (India)</h1>
          <p>
            Calculate House Rent Allowance (HRA) exemption under Section 10(13A) of Income Tax Act
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Salary & Rent Details
              </h2>

              {/* Calculation Type */}
              <div className="input-group">
                <label>Calculation Period</label>
                <div className="quick-select-group">
                  {[
                    { value: "monthly", label: "Monthly" },
                    { value: "annual", label: "Annual" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Basic Salary */}
              <div className="input-group">
                <label>Basic Salary {calculationType === "monthly" ? "(per month)" : "(per annum)"}</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={basicSalary}
                    onChange={(e) => setBasicSalary(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {calculationType === "monthly" 
                    ? [30000, 50000, 75000, 100000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setBasicSalary(amount)}
                        className="quick-select-btn primary"
                      >
                        ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                      </button>
                    ))
                    : [600000, 1000000, 1500000, 2000000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setBasicSalary(amount)}
                        className="quick-select-btn primary"
                      >
                        ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                      </button>
                    ))
                  }
                </div>
              </div>

              {/* HRA Received */}
              <div className="input-group">
                <label>HRA Received {calculationType === "monthly" ? "(per month)" : "(per annum)"}</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={hraReceived}
                    onChange={(e) => setHraReceived(e.target.value)}
                    className="calc-input"
                    placeholder="20000"
                  />
                </div>
                <div className="quick-select-group">
                  {calculationType === "monthly"
                    ? [10000, 20000, 30000, 40000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setHraReceived(amount)}
                        className="quick-select-btn success"
                      >
                        ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                      </button>
                    ))
                    : [200000, 400000, 600000, 800000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setHraReceived(amount)}
                        className="quick-select-btn success"
                      >
                        ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                      </button>
                    ))
                  }
                </div>
              </div>

              {/* Rent Paid */}
              <div className="input-group">
                <label>Rent Paid {calculationType === "monthly" ? "(per month)" : "(per annum)"}</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={rentPaid}
                    onChange={(e) => setRentPaid(e.target.value)}
                    className="calc-input"
                    placeholder="15000"
                  />
                </div>
                <div className="quick-select-group">
                  {calculationType === "monthly"
                    ? [10000, 15000, 25000, 35000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setRentPaid(amount)}
                        className="quick-select-btn secondary"
                      >
                        ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                      </button>
                    ))
                    : [150000, 250000, 350000, 500000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setRentPaid(amount)}
                        className="quick-select-btn secondary"
                      >
                        ₹{amount >= 100000 ? `${(amount / 100000).toFixed(1)}L` : `${(amount / 1000).toFixed(0)}K`}
                      </button>
                    ))
                  }
                </div>
              </div>

              {/* City Type */}
              <div className="input-group">
                <label>City Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "metro", label: "Metro City (50%)" },
                    { value: "nonmetro", label: "Non-Metro (40%)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCityType(type.value)}
                      className={`quick-select-btn ${cityType === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateHRA} className="calculate-btn">
                Calculate HRA Exemption
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                HRA Exemption Breakdown
              </h2>

              <div className="result-card">
                {/* HRA Exemption - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">HRA Exemption (Tax Free)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.hraExemption).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.exemptionPercentage}% of HRA is exempt ({calculationType === "monthly" ? "per month" : "per annum"})
                  </div>
                </div>

                {/* Taxable HRA */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Taxable HRA</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.taxableHRA).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Subject to income tax
                  </div>
                </div>

                {/* Actual HRA */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Actual HRA Received</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.actualHRA).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Total HRA component
                  </div>
                </div>

                {/* Rent Paid */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Rent Paid</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.rentPaid).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Annual rent payment
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">HRA Exemption Calculation (Least of Three)</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">1. Actual HRA Received</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.actualHRA).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">2. {cityType === "metro" ? "50%" : "40%"} of Basic Salary</span>
                      <span className="breakdown-percentage">
                        ₹{Number(cityType === "metro" ? result.fiftyPercentBasic : result.fortyPercentBasic).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(cityType === "metro" ? result.fiftyPercentBasic : result.fortyPercentBasic) / parseFloat(result.actualHRA)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">3. Rent - 10% of Basic</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.rentMinusTenPercent).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: `${(parseFloat(result.rentMinusTenPercent) / parseFloat(result.actualHRA)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* HRA Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">HRA Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Tax-Free HRA</span>
                      <span className="breakdown-percentage">
                        {result.exemptionPercentage}% (₹{Number(result.hraExemption).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${result.exemptionPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Taxable HRA</span>
                      <span className="breakdown-percentage">
                        {(100 - parseFloat(result.exemptionPercentage)).toFixed(2)}% (₹{Number(result.taxableHRA).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${100 - parseFloat(result.exemptionPercentage)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Saving Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Tax Saving Tip</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Remember to keep rent receipts and rental agreement as proof for HRA claim.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding HRA Exemption in India</h3>
              <p>
                House Rent Allowance (HRA) is a component of salary paid by employers to employees for accommodation 
                expenses. Under Section 10(13A) of the Income Tax Act, a portion of HRA is exempt from tax if you live 
                in rented accommodation. The exemption is calculated as the minimum of three values: actual HRA received, 
                50% of basic salary for metro cities (Delhi, Mumbai, Kolkata, Chennai) or 40% for non-metro cities, and 
                rent paid minus 10% of basic salary. To claim HRA exemption, you must submit rent receipts to your 
                employer. If annual rent exceeds ₹1 lakh, landlord's PAN is mandatory. Basic salary includes basic pay 
                and dearness allowance but excludes other allowances and perquisites.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>HRA Exemption Formula:</h4>
            <div className="formula-display">
              HRA Exemption = Minimum of (Actual HRA, 50%/40% of Basic, Rent - 10% of Basic)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Actual HRA</div>
                <div className="formula-param-value">HRA received from employer</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">50% or 40% of Basic</div>
                <div className="formula-param-value">50% for metro, 40% for non-metro cities</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Rent - 10% of Basic</div>
                <div className="formula-param-value">Actual rent paid minus 10% of basic salary</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Taxable HRA</div>
                <div className="formula-param-value">Actual HRA - HRA Exemption</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Which cities are considered metro for HRA calculation?</h5>
              <p>
                Delhi, Mumbai, Kolkata, and Chennai are metro cities (50% of basic). All other cities are non-metro (40% of basic).
              </p>
            </div>

            <div className="faq-item">
              <h5>What documents are needed to claim HRA exemption?</h5>
              <p>
                Rent receipts, rental agreement, and landlord's PAN (if annual rent exceeds ₹1 lakh) are required. Submit these to your employer.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I claim HRA if I live in my own house?</h5>
              <p>
                No, HRA exemption is only available if you live in rented accommodation and pay rent. Own house doesn't qualify for HRA exemption.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is included in basic salary for HRA calculation?</h5>
              <p>
                Basic salary includes basic pay plus dearness allowance (DA). It excludes special allowances, bonuses, commissions, and other perquisites.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I claim HRA and home loan deduction together?</h5>
              <p>
                No, if you live in your own house and pay home loan, you can't claim HRA. But you can claim HRA for one city and home loan for another.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I don't receive HRA in my salary?</h5>
              <p>
                You can claim deduction under Section 80GG (up to ₹5,000/month) if you don't receive HRA but pay rent. Submit Form 10BA to employer.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRAExemptionCalculator;